import * as React from 'react';
import styled from 'styled-components';
import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import H3 from '@sdk/components/ui/typography/H3';
import Text16 from '@sdk/components/ui/typography/Text16';
import Text14 from '@sdk/components/ui/typography/Text14';
import Button from '@sdk/components/ui/buttons/Button';
import Href from '@sdk/components/ui/Href';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import useConfig from '@sdk/config/useConfig';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { Feature } from '@sdk/common/features';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';

import { FeatureNames } from '@web/account/types';

import usePremiumGuard from '../../../premiumGuard/usePremiumGuard';
import { TimeclockFeaturePreview } from '../../../premiumGuard/premiumPreview/TimeclockFeaturePreview';
import { CustomerReviews } from '../../../premiumGuard/premiumPreview/CustomerReviews';
import { OtherPremiumFeatures } from '../../../premiumGuard/premiumPreview/OtherPremiumFeatures';
import bgImage from '../../../assets/images/premium-locked-screen/timeclock/header.png';

const PremiumFeaturePreview = styled(Column)`
  flex: 1;
  background-color: ${({ theme }): string => theme.colors.white};
  box-shadow: 0 2px 8px 0 ${({ theme }): string => theme.colors.boxShadow};
`;

const PreviewBanner = styled(Row)`
  flex: 1;
  background: url(${bgImage}) no-repeat;
  background-size: contain;
  background-position: right top;
  padding: 40px 40px 40px 75px;
  border-radius: 4px;
`;

const PreviewBannerContent = styled(Column)`
  width: 360px;
`;

type PremiumPreviewContentProps = {
  themeColor: ThemeColorType;
};
const PremiumPreviewContent = styled(Row)<PremiumPreviewContentProps>`
  background-color: ${({ theme, themeColor }): string => theme.colors[themeColor]};
`;

const GutterColumn = styled(Column)`
  flex: 1;
`;

const PremiumPreviewContentColumn = styled(Column)`
  flex: 3;
`;

export const TimeClockPremiumScreen: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { currentUser } = useAuth();
  const { APP_URL } = useConfig();
  const { getRequiredPlan, openPricingModal } = usePremiumGuard();

  const purchasePremium = (): void => {
    window.location.href = `${APP_URL}/account/purchase`;
  };

  const goToBilling = (): void => {
    window.location.href = `${APP_URL}/account/billing`;
  };

  return (
    <PremiumFeaturePreview>
      <PreviewBanner>
        <PreviewBannerContent>
          <Row pTop={20} pBottom={10}>
            <H3 themeColor={ThemeColorType.primary}>{gettext('Streamline time and attendance')}</H3>
          </Row>
          <Row pBottom={10}>
            <Text16 bold themeColor={ThemeColorType.gray}>
              {gettext('Available in %(subscriptionName)s', {
                subscriptionName: getRequiredPlan(Feature.clockIn),
              })}
            </Text16>
          </Row>
          <Row pBottom={10}>
            <Text14 themeColor={ThemeColorType.gray}>
              {gettext(
                'Easily track employee hours, review timesheets and export payroll reports for seamless payroll processing.'
              )}
            </Text14>
          </Row>
          {currentUser.org.isToastBilling ? (
            <Href
              fontSize={14}
              semiBold
              themeColor={ThemeColorType.primary}
              onClick={() => goToBilling()}>
              {gettext('Learn more about Scheduling Pro >')}
            </Href>
          ) : (
            <Button onClick={purchasePremium} buttonType={ButtonType.primary}>
              {gettext('Purchase premium')}
            </Button>
          )}
        </PreviewBannerContent>
      </PreviewBanner>

      <PremiumPreviewContent themeColor={ThemeColorType.white}>
        <GutterColumn />
        <PremiumPreviewContentColumn>
          <TimeclockFeaturePreview />
        </PremiumPreviewContentColumn>
        <GutterColumn />
      </PremiumPreviewContent>

      <PremiumPreviewContent themeColor={ThemeColorType.bodyBg}>
        <GutterColumn />
        <PremiumPreviewContentColumn>
          <CustomerReviews purchasePremium={purchasePremium} />
        </PremiumPreviewContentColumn>
        <GutterColumn />
      </PremiumPreviewContent>

      <OtherPremiumFeatures
        user={currentUser}
        hiddenKeys={[FeatureNames.timeclock]}
        openPricingModal={openPricingModal}
      />
    </PremiumFeaturePreview>
  );
};
